/**
 * Bridge between CSS design tokens and JS-driven libraries (Recharts).
 * Resolves a `--color-*` variable to a usable `rgb()` string at call time so
 * charts follow the active light/dark theme.
 */

/** Read a CSS custom property off :root and return an `rgb()` string. */
export function cssColor(variable: string, alpha = 1): string {
  if (typeof window === "undefined") return "rgb(0 0 0)";
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .trim();
  if (!raw) return "rgb(0 0 0)";
  return alpha === 1 ? `rgb(${raw})` : `rgb(${raw} / ${alpha})`;
}

/**
 * A faint wash of a colour that is already a resolved `rgb(r g b)` string (from
 * `chartColors` or `statusRegistry`), for a badge or banner background.
 *
 * Never append an alpha suffix instead: `` `${color}14` `` yields `rgb(r g b)14`,
 * invalid CSS that the browser drops silently, so the element renders with no
 * background at all. Anything that is not an opaque `rgb()` passes through.
 */
export function tint(color: string, alpha = 0.12): string {
  const match = /^rgb\(([^/)]+)\)$/.exec(color.trim());
  return match ? `rgb(${match[1].trim()} / ${alpha})` : color;
}

/** Semantic token accessors for charts. */
export const chartColors = {
  brand: (a = 1) => cssColor("--color-brand", a),
  fg: (a = 1) => cssColor("--color-fg", a),
  fgMuted: (a = 1) => cssColor("--color-fg-muted", a),
  border: (a = 1) => cssColor("--color-border", a),
  ok: (a = 1) => cssColor("--color-status-ok", a),
  warn: (a = 1) => cssColor("--color-status-warn", a),
  critical: (a = 1) => cssColor("--color-status-critical", a),
  leak: (a = 1) => cssColor("--color-status-leak", a),
  anomaly: (a = 1) => cssColor("--color-status-anomaly", a),
  offline: (a = 1) => cssColor("--color-status-offline", a),
  irregular: (a = 1) => cssColor("--color-status-irregular", a),
  supplyWindow: (a = 1) => cssColor("--color-status-supply-window", a),
  /** Categorical slot 1-8 (localities / user types). Wraps within 1-8. */
  cat: (i: number, a = 1) => cssColor(`--color-cat-${((i - 1) % 8) + 1}`, a),
};

/** Number of categorical slots available (see --color-cat-N in tokens.css). */
export const CATEGORICAL_SLOTS = 8;
